import { Save, TimerReset, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { saveRoutineLog } from "../../firebase/firestore.js";
import { getNowTimeValue } from "../../utils/date.js";
import { matchesSearch } from "../../utils/search.js";
import { formatTime, timeRangeLabel } from "../../utils/time.js";

function roomCanBeEntered(roomNumber) {
  const value = String(roomNumber || "").trim().toUpperCase();
  return !value || value === "N/A";
}

export default function PendingOut() {
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const { user } = useAuth();
  const { showToast } = useToast();
  const { date, day, routines, logs, settings, loading, pendingCount, routineMasters } = useOutletContext();

  const routineById = useMemo(() => Object.fromEntries(routines.map((routine) => [routine.id, routine])), [routines]);
  const pendingLogs = useMemo(() => {
    return logs
      .filter((log) => log.actualIn && !log.actualOut && log.classStatus !== "suspended")
      .map((log) => ({ log, routine: routineById[log.routineId] || { ...log, id: log.routineId } }))
      .filter(({ log }) =>
        matchesSearch(
          {
            teacherName: log.teacherName,
            roomNumber: log.roomNumber,
            className: log.className,
            sectionName: log.sectionName,
            subjectName: log.subjectName,
            scheduledIn: log.scheduledIn,
            scheduledOut: log.scheduledOut,
            actualIn: log.actualIn,
            day: log.day,
            date: log.date,
            status: "Pending OUT"
          },
          search,
          [
            "teacherName",
            "roomNumber",
            "className",
            "sectionName",
            "subjectName",
            "scheduledIn",
            "scheduledOut",
            "actualIn",
            "day",
            "date",
            "status"
          ]
        )
      )
      .sort((a, b) => String(a.log.roomNumber || "").localeCompare(String(b.log.roomNumber || ""), undefined, { numeric: true }));
  }, [logs, routineById, search]);

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const roomPatch = roomCanBeEntered(modal.routine?.roomNumber || modal.log?.roomNumber) && modal.roomNumber?.trim()
        ? { roomNumber: modal.roomNumber.trim() }
        : {};
      await saveRoutineLog({
        routine: modal.routine,
        date,
        day,
        user,
        settings,
        patch: {
          actualIn: modal.actualIn,
          actualOut: modal.actualOut,
          ...(modal.teacherName ? { teacherName: modal.teacherName } : {}),
          ...roomPatch,
          classStatus: "normal",
          suspensionReason: ""
        }
      });
      showToast("OUT time saved");
      setModal(null);
    } catch (error) {
      showToast(error.message || "Unable to save OUT time", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="page-hero compact">
        <p>Today only · {date}</p>
        <h2>Pending OUT {pendingCount ? `(${pendingCount})` : ""}</h2>
      </section>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search room, teacher, class, section, subject, time, day or status"
      />
      {loading ? (
        <SkeletonList />
      ) : pendingLogs.length ? (
        <div className="routine-list">
          {pendingLogs.map(({ log, routine }) => (
            <article className="card pending-card" key={log.id}>
              <div>
                <p className="eyebrow">Room {log.roomNumber}</p>
                <h3>{log.teacherName}</h3>
                <p>
                  {log.className} - {log.sectionName} · {log.subjectName}
                </p>
                <p>Schedule: {timeRangeLabel(log.scheduledIn, log.scheduledOut)}</p>
                <p>IN Time: {formatTime(log.actualIn)}</p>
              </div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() =>
                  setModal({
                    routine,
                    log,
                    actualIn: log.actualIn,
                    actualOut: log.actualOut || getNowTimeValue(),
                    teacherName: log.teacherName || routine.teacherName || "",
                    roomNumber: roomCanBeEntered(log.roomNumber || routine.roomNumber) ? "" : log.roomNumber || routine.roomNumber || ""
                  })
                }
              >
                <TimerReset size={17} />
                Add OUT Time
              </button>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          icon="🎉"
          title={search ? "No matching pending OUT" : "No pending OUT"}
          text={search ? "Try another room, teacher, subject or status." : "Every IN-marked class for today has an OUT time."}
        />
      )}
      <Modal open={Boolean(modal)} title="Add OUT Time" onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <div className="form-grid two">
            <Field label="Room number">
              <input
                value={modal?.roomNumber || ""}
                onChange={(event) => setModal((value) => ({ ...value, roomNumber: event.target.value }))}
                placeholder="Room number"
              />
            </Field>
            <Field label="Teacher">
              <select value={modal?.teacherName || ""} onChange={(event) => setModal((value) => ({ ...value, teacherName: event.target.value }))} required>
                <option value="">Select teacher</option>
                {(routineMasters?.teachers || []).map((teacher) => (
                  <option key={teacher.id} value={teacher.name}>{teacher.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="IN Time">
            <input
              type="time"
              value={modal?.actualIn || ""}
              onChange={(event) => setModal((value) => ({ ...value, actualIn: event.target.value }))}
              required
            />
          </Field>
          <Field label="OUT Time">
            <input
              type="time"
              value={modal?.actualOut || ""}
              onChange={(event) => setModal((value) => ({ ...value, actualOut: event.target.value }))}
              required
            />
          </Field>
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}>
              <X size={17} />
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? <span className="spinner" /> : <Save size={17} />}
              Save OUT
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
